import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Heart, MapPin, GraduationCap, ShieldCheck, Eye } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';
import { useState } from 'react';

export default function ProfileCard({ profile }) {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [interestSent, setInterestSent] = useState(false);

  const calcAge = (dob) => {
    if (!dob) return '';
    return Math.floor((Date.now() - new Date(dob).getTime()) / (365.25 * 24 * 60 * 60 * 1000));
  };

  const handleSendInterest = async (e) => {
    e.stopPropagation();
    try {
      await api.post('/interests', { to_user_id: profile.id });
      setInterestSent(true);
      toast.success('Interest sent!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send interest');
    }
  };

  const age = calcAge(profile.dob);

  return (
    <Card
      className="rounded-2xl border border-[#E7E5E4] overflow-hidden bg-white profile-card-hover cursor-pointer group"
      onClick={() => navigate(`/profile/${profile.id}`)}
      data-testid={`profile-card-${profile.id}`}
    >
      {/* Photo */}
      <div className="aspect-[4/3] bg-gradient-to-br from-[#D9381E]/10 to-[#F2A900]/10 relative overflow-hidden">
        {profile.photos?.length > 0 ? (
          <img src={profile.photos[0]} alt={profile.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <span className="text-5xl font-bold text-[#D9381E]/20">{profile.name?.[0]}</span>
          </div>
        )}
        {profile.verified_badge && (
          <Badge className="absolute top-3 left-3 bg-green-600 text-white text-[10px] gap-1">
            <ShieldCheck className="w-3 h-3" /> {t('profile.verified')}
          </Badge>
        )}
        {profile.subscription?.plan && profile.subscription.plan !== 'free' && (
          <Badge className="absolute top-3 right-3 bg-[#F2A900] text-[#2D2424] text-[10px]">
            Premium
          </Badge>
        )}
      </div>

      {/* Info */}
      <div className="p-4">
        <div className="flex items-start justify-between mb-2">
          <div>
            <h3 className="font-bold text-[#2D2424] truncate">{profile.name}</h3>
            <p className="text-xs text-[#78716C]">
              {age && `${age} ${t('common.years')}`}{profile.height ? ` | ${profile.height} cm` : ''}
            </p>
          </div>
        </div>

        <div className="space-y-1.5 mb-3">
          {profile.city && (
            <div className="flex items-center gap-1.5 text-xs text-[#78716C]">
              <MapPin className="w-3 h-3" /> {profile.city}{profile.state ? `, ${profile.state}` : ''}
            </div>
          )}
          {profile.education_degree && (
            <div className="flex items-center gap-1.5 text-xs text-[#78716C]">
              <GraduationCap className="w-3 h-3" /> {profile.education_degree}{profile.occupation ? ` | ${profile.occupation}` : ''}
            </div>
          )}
          {profile.religion && (
            <div className="flex items-center gap-1.5 text-xs text-[#78716C]">
              <Eye className="w-3 h-3" /> {profile.religion}{profile.caste ? ` - ${profile.caste}` : ''}
            </div>
          )}
        </div>

        <Button
          size="sm"
          onClick={handleSendInterest}
          disabled={interestSent}
          className={`w-full rounded-full text-xs font-semibold transition-all ${
            interestSent
              ? 'bg-green-100 text-green-700 border border-green-200'
              : 'bg-[#D9381E] hover:bg-[#B91C1C] text-white shadow-sm'
          }`}
          data-testid={`send-interest-${profile.id}`}
        >
          <Heart className={`w-3 h-3 mr-1 ${interestSent ? 'fill-green-700' : ''}`} />
          {interestSent ? t('profile.interest_sent') : t('profile.send_interest')}
        </Button>
      </div>
    </Card>
  );
}
